// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import AccountManager from '@app/features/auth/state/AccountManager';
import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import GuildNSFWAgree from '@app/features/guild/state/GuildNSFWAgree';
import Guilds from '@app/features/guild/state/Guilds';
import {getParserFlagsForContext} from '@app/features/messaging/components/markdown/renderers';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import {Message} from '@app/features/messaging/models/Message';
import * as MessageUtils from '@app/features/messaging/utils/MessageUtils';
import {parseAndRenderToPlaintext} from '@app/features/messaging/utils/markdown/Plaintext';
import {SystemMessageUtils} from '@app/features/messaging/utils/SystemMessageUtils';
import Navigation from '@app/features/navigation/state/Navigation';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import * as NotificationUtils from '@app/features/notification/utils/NotificationUtils';
import * as PushSubscriptionService from '@app/features/platform/push/PushSubscriptionService';
import {IS_DEV} from '@app/features/platform/types/Env';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent, stopPersistent} from '@app/features/platform/utils/MobXPersistence';
import LocalPresence from '@app/features/presence/state/LocalPresence';
import type {RelationshipWire} from '@app/features/relationship/models/Relationship';
import FriendsTab from '@app/features/relationship/state/FriendsTab';
import Relationships from '@app/features/relationship/state/Relationships';
import {isInstalledPwa} from '@app/features/ui/utils/PwaUtils';
import type {User} from '@app/features/user/models/User';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import Users from '@app/features/user/state/Users';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {FAVORITES_GUILD_ID as ME} from '@app/vendor/constants/AppConstants';
import {ChannelTypes, MessageFlags, MessageTypes} from '@app/vendor/constants/ChannelConstants';
import {MessageNotifications} from '@app/vendor/constants/NotificationConstants';
import {StatusTypes} from '@app/vendor/constants/StatusConstants';
import {RelationshipTypes} from '@app/vendor/constants/UserConstants';
import type {Message as WireMessage} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {LRUCache} from 'lru-cache';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

const logger = new Logger('Notification');
const shouldManagePushSubscriptions = (): boolean => isInstalledPwa();
export enum TTSNotificationMode {
	FOR_ALL_CHANNELS = 0,
	FOR_CURRENT_CHANNEL = 1,
	NEVER = 2,
}
const MAX_PER_CHANNEL = 5;
const CACHE_SIZE = 500;
interface TrackedNotification {
	browserNotification: Notification | null;
	nativeId: string | null;
}
const notificationTracker = new (class {
	private channels: Record<string, Array<TrackedNotification>> = {};
	track(channelId: string, notification: TrackedNotification): void {
		let notifications = this.channels[channelId];
		if (notifications == null) {
			notifications = [];
			this.channels[channelId] = notifications;
		}
		notifications.push(notification);
		while (notifications.length > MAX_PER_CHANNEL) {
			const old = notifications.shift();
			if (old) {
				old.browserNotification?.close();
				if (old.nativeId) {
					NotificationUtils.closeNativeNotification(old.nativeId);
				}
			}
		}
	}
	clearChannel(channelId: string): void {
		const notifications = this.channels[channelId];
		if (notifications == null) return;
		delete this.channels[channelId];
		const browserNotifications = notifications
			.map((n) => n.browserNotification)
			.filter((n): n is Notification => n != null);
		browserNotifications.forEach((notification) => notification.close());
		const nativeIds = notifications.map((n) => n.nativeId).filter((id): id is string => id != null);
		if (nativeIds.length > 0) {
			NotificationUtils.closeNativeNotifications(nativeIds);
		}
	}
})();
type NotificationData = Readonly<{
	message: WireMessage;
	messageRecord: Message;
	currentUser: User;
	user: User;
	channel: Channel;
}>;
const isDocumentFocused = (): boolean => {
	if (typeof document === 'undefined') {
		return false;
	}
	if (document.hidden) {
		return false;
	}
	return typeof document.hasFocus === 'function' && document.hasFocus();
};
class NotificationState {
	browserNotificationsEnabled = false;
	unreadMessageBadgeEnabled = true;
	ttsNotificationMode: TTSNotificationMode = TTSNotificationMode.NEVER;
	focused = isDocumentFocused();
	notifiedMessageIds = new LRUCache<string, boolean>({max: CACHE_SIZE});
	private isPersisting = false;
	private accountReactionDisposer: (() => void) | null = null;
	private i18n: I18n | null = null;
	constructor() {
		makeAutoObservable(
			this,
			{
				notifiedMessageIds: false,
			},
			{autoBind: true},
		);
		void this.initPersistence().then(() => {
			void this.refreshPermission();
		});
		queueMicrotask(() => {
			NotificationUtils.ensureDesktopNotificationClickHandler();
		});
		queueMicrotask(() => {
			this.accountReactionDisposer = reaction(
				() => {
					try {
						return AccountManager?.currentUserId;
					} catch {
						return undefined;
					}
				},
				() => {
					if (!shouldManagePushSubscriptions()) return;
					if (!this.browserNotificationsEnabled) return;
					void PushSubscriptionService.registerPushSubscription();
				},
			);
		});
		if (IS_DEV) {
			window.__notificationCleanup = () => this.cleanup();
		}
	}
	setI18n(i18n: I18n): void {
		this.i18n = i18n;
	}
	private async initPersistence(): Promise<void> {
		if (this.isPersisting) return;
		this.isPersisting = true;
		await makePersistent(this, 'Notification', [
			'browserNotificationsEnabled',
			'unreadMessageBadgeEnabled',
			'ttsNotificationMode',
		]);
	}
	private cleanup(): void {
		if (!this.isPersisting) return;
		stopPersistent('Notification', this);
		this.isPersisting = false;
		this.accountReactionDisposer?.();
		this.accountReactionDisposer = null;
	}
	getUnreadMessageBadgeEnabled(): boolean {
		return this.unreadMessageBadgeEnabled;
	}
	getBrowserNotificationsEnabled(): boolean {
		return this.browserNotificationsEnabled;
	}
	getTTSNotificationMode(): TTSNotificationMode {
		return this.ttsNotificationMode;
	}
	setTTSNotificationMode(mode: TTSNotificationMode): void {
		this.ttsNotificationMode = mode;
	}
	isFocused(): boolean {
		return this.focused;
	}
	private isMessageMentionLike(channel: Channel, message: Message, currentUser: User): boolean {
		if (MessageUtils.isMentioned(currentUser, message)) {
			return true;
		}
		if (channel.isPrivate()) {
			return !UserGuildSettings.isGuildOrChannelMuted(null, channel.id);
		}
		return false;
	}
	private shouldNotifyBasedOnSettings(channel: Channel, messageRecord: Message, currentUser: User): boolean {
		const level = UserGuildSettings.resolvedMessageNotifications({
			id: channel.id,
			guildId: channel.guildId,
			parentId: channel.parentId ?? undefined,
			type: channel.type,
		});
		if (level === MessageNotifications.NO_MESSAGES) {
			return false;
		}
		if (level === MessageNotifications.ALL_MESSAGES) {
			return true;
		}
		return this.isMessageMentionLike(channel, messageRecord, currentUser);
	}
	private isFocusedForNotifications(): boolean {
		return isDocumentFocused() || this.focused;
	}
	private getVisibleChannelId(): string | null {
		return Navigation.channelId ?? SelectedChannel.currentChannelId;
	}
	private isViewingChannel(channelId: string): boolean {
		return this.getVisibleChannelId() === channelId;
	}
	private validateNotificationData(message: WireMessage): NotificationData | null {
		const channel = Channels.getChannel(message.channel_id);
		if (!channel) return null;
		const user = Users.getUser(message.author.id);
		if (!user) return null;
		if (message.author.id === Authentication.currentUserId) return null;
		if (Relationships.isBlocked(user.id)) return null;
		if (LocalPresence.getStatus() === StatusTypes.DND) return null;
		if ((message.flags & MessageFlags.SUPPRESS_NOTIFICATIONS) === MessageFlags.SUPPRESS_NOTIFICATIONS) {
			return null;
		}
		if (
			UserGuildSettings.allowNoMessages({
				id: channel.id,
				guildId: channel.guildId,
				parentId: channel.parentId ?? undefined,
				type: channel.type,
			})
		) {
			return null;
		}
		if (this.notifiedMessageIds.has(message.id)) {
			return null;
		}
		if (GuildNSFWAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null})) {
			return null;
		}
		const currentUser = Users.getCurrentUser();
		if (!currentUser) return null;
		const messageRecord = new Message(message, {skipUserCache: false});
		return {message, messageRecord, currentUser, user, channel};
	}
	private markNotified(key: string): void {
		const newCache = new LRUCache<string, boolean>({max: CACHE_SIZE});
		this.notifiedMessageIds.forEach((value, k) => newCache.set(k, value));
		newCache.set(key, true);
		this.notifiedMessageIds = newCache;
	}
	private async showBackgroundNotification(data: NotificationData): Promise<void> {
		if (!this.i18n) {
			throw new Error('Notification: i18n not initialized');
		}
		const {message, user, channel} = data;
		NotificationUtils.playNotificationSoundIfEnabled();
		if (!this.browserNotificationsEnabled) {
			this.markNotified(message.id);
			return;
		}
		const useMacOSNotificationPresentation = NotificationUtils.isMacOSDesktopNotification();
		if (this.isFocusedForNotifications() && useMacOSNotificationPresentation) {
			this.markNotified(message.id);
			return;
		}
		let title = NicknameUtils.getNickname(user, channel.guildId);
		let subtitle: string | undefined;
		switch (channel.type) {
			case ChannelTypes.GUILD_TEXT:
				if (message.type === MessageTypes.DEFAULT) {
					if (useMacOSNotificationPresentation) {
						const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : null;
						subtitle = guild ? `${guild.name} #${channel.name}` : `#${channel.name}`;
					} else {
						title = `${title} (#${channel.name})`;
					}
				} else {
					const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : null;
					if (guild) {
						if (useMacOSNotificationPresentation) {
							title = guild.name;
							subtitle = `#${channel.name}`;
						} else {
							title = `${guild.name} (#${channel.name})`;
						}
					}
				}
				break;
			case ChannelTypes.GROUP_DM:
				if (useMacOSNotificationPresentation) {
					subtitle = channel.name || 'Group DM';
				} else {
					title = `${title} (${channel.name || 'Group DM'})`;
				}
				break;
		}
		let body = '';
		const isUserMessage =
			message.type === MessageTypes.DEFAULT ||
			message.type === MessageTypes.REPLY ||
			message.type === MessageTypes.CLIENT_SYSTEM;
		if (!isUserMessage) {
			body = SystemMessageUtils.stringify(message, this.i18n) || '';
		} else {
			body = parseAndRenderToPlaintext(
				message.content,
				getParserFlagsForContext(MarkdownContext.STANDARD_WITHOUT_JUMBO),
				{
					channelId: channel.id,
					preserveMarkdown: true,
					includeEmojiNames: true,
				},
			);
		}
		if (!body && message.attachments?.length) {
			body = this.i18n._(msg`Attachment: ${message.attachments[0].filename}`);
		}
		if (!body && message.embeds?.length) {
			const embed = message.embeds[0];
			if (embed.description) {
				body = embed.title ? `${embed.title}: ${embed.description}` : embed.description;
			} else if (embed.title) {
				body = embed.title;
			} else if (embed.fields?.length) {
				const field = embed.fields[0];
				body = `${field.name}: ${field.value}`;
			}
		}
		const notificationUrl =
			channel.guildId && channel.guildId !== ME
				? Routes.channelMessage(channel.guildId, channel.id, message.id)
				: Routes.dmChannelMessage(channel.id, message.id);
		try {
			const result = await NotificationUtils.showNotification({
				title,
				subtitle,
				body,
				icon: AvatarUtils.getUserNotificationAvatarURL(user),
				url: notificationUrl,
				playSound: false,
			});
			notificationTracker.track(channel.id, {
				browserNotification: result.browserNotification,
				nativeId: result.nativeNotificationId,
			});
			this.markNotified(message.id);
		} catch (error) {
			logger.error('Failed to show notification', {messageId: message.id, channelId: channel.id}, error);
			this.markNotified(message.id);
		}
	}
	handleMessageCreate({message}: {message: WireMessage}): boolean {
		if (
			message.author.id !== Authentication.currentUserId &&
			!Relationships.isBlocked(message.author.id) &&
			Channels.getChannel(message.channel_id) != null &&
			this.isViewingChannel(message.channel_id) &&
			this.isFocusedForNotifications()
		) {
			NotificationUtils.playSameChannelNotificationSoundIfEnabled();
			this.markNotified(message.id);
			return true;
		}
		const notificationData = this.validateNotificationData(message);
		if (!notificationData) {
			return false;
		}
		const {channel, messageRecord, currentUser} = notificationData;
		if (!this.shouldNotifyBasedOnSettings(channel, messageRecord, currentUser)) {
			return false;
		}
		void this.showBackgroundNotification(notificationData);
		return true;
	}
	handleNotificationPermissionGranted(): void {
		this.browserNotificationsEnabled = true;
		if (shouldManagePushSubscriptions()) {
			void PushSubscriptionService.registerPushSubscription();
		}
	}
	handleNotificationPermissionDenied(): void {
		this.browserNotificationsEnabled = false;
		if (shouldManagePushSubscriptions()) {
			void PushSubscriptionService.unregisterAllPushSubscriptions();
		}
	}
	async refreshPermission(): Promise<void> {
		try {
			const granted = await NotificationUtils.isGranted();
			if (!granted) {
				runInAction(() => {
					this.browserNotificationsEnabled = false;
				});
				return;
			}
			if (this.browserNotificationsEnabled && shouldManagePushSubscriptions()) {
				void PushSubscriptionService.registerPushSubscription();
			}
		} catch (error) {
			logger.error('Failed to refresh notification permission', error);
		}
	}
	handleNotificationSoundToggle(enabled: boolean): void {
		this.unreadMessageBadgeEnabled = enabled;
	}
	handleWindowFocus({focused}: {focused: boolean}): void {
		this.focused = focused;
		if (focused) {
			const channelId = SelectedChannel.currentChannelId;
			if (channelId) {
				notificationTracker.clearChannel(channelId);
			}
		}
	}
	handleChannelSelect({channelId}: {channelId?: string | null}): void {
		if (channelId) {
			notificationTracker.clearChannel(channelId);
		}
	}
	handleMessageAck({channelId}: {channelId: string}): void {
		notificationTracker.clearChannel(channelId);
	}
	handleMessageDelete({channelId}: {channelId: string}): void {
		notificationTracker.clearChannel(channelId);
	}
	handleRelationshipNotification(
		relationship: RelationshipWire,
		options?: {
			event?: 'add' | 'update';
		},
	): void {
		if (!this.i18n) {
			throw new Error('Notification: i18n not initialized');
		}
		if (!this.browserNotificationsEnabled) {
			return;
		}
		if (LocalPresence.getStatus() === StatusTypes.DND) {
			return;
		}
		const user = Users.getUser(relationship.user?.id ?? relationship.id);
		if (!user) {
			return;
		}
		const cacheKey = `relationship_${relationship.type}_${user.id}`;
		if (this.notifiedMessageIds.has(cacheKey)) {
			return;
		}
		if (options?.event === 'update') {
			return;
		}
		let title: string;
		let body: string;
		if (relationship.type === RelationshipTypes.INCOMING_REQUEST) {
			title = this.i18n._(msg`Friend Request`);
			body = this.i18n._(msg`${user.displayName} sent you a friend request`);
			FriendsTab.setTab('pending');
		} else if (relationship.type === RelationshipTypes.FRIEND) {
			title = this.i18n._(msg`Friend Added`);
			body = this.i18n._(msg`${user.displayName} is now your friend!`);
		} else {
			return;
		}
		if (this.focused && NotificationUtils.isMacOSDesktopNotification()) {
			this.markNotified(cacheKey);
			return;
		}
		void NotificationUtils.showNotification({
			title,
			body,
			icon: AvatarUtils.getUserNotificationAvatarURL(user),
			url: Routes.ME,
		}).catch((error) => {
			logger.error('Failed to show relationship notification', {cacheKey}, error);
		});
		this.markNotified(cacheKey);
	}
}
export default new NotificationState();
